import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Button from '@/components/CustomButtons/Button';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useGetClientBankAccounts } from '@/api/hooks/client-bank-accounts/useGetClientBankAccounts';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import { formatBankAccountDisplay } from '@/utils/bank-account.util';

export default function BankAccountLinkage({
  formValues,
  setFormValues,
  setUpdatingBlocks,
  updatingBlocks,
  editId,
  isUpdating,
  onBankAccountLinkageCancel,
  updateBankAccountLinkage,
  organizationId,
}) {
  const { t } = useTranslation();

  const hasPermission = checkPermission({
    parentKey: 'client_bank_accounts',
    childKey: 'view_bank_accounts',
    permission: 'read',
  });

  const {
    data: bankAccounts = [],
    loading: loadingBankAccounts,
    error: bankAccountsError,
  } = useGetClientBankAccounts(organizationId, {
    enabled: hasPermission && !!organizationId,
  });

  const activeBankAccounts = useMemo(() => {
    return bankAccounts.filter((account) => !account.deactivated_at);
  }, [bankAccounts]);

  const handleBankAccountChange = (event) => {
    const selectedId = event.target.value;
    const selectedAccount = activeBankAccounts.find(
      (acc) => acc.id === selectedId,
    );

    setFormValues((prev) => ({
      ...prev,
      client_bank_account_id: selectedId || null,
      bank_account_display_name: selectedAccount?.display_name || null,
      bank_account_business_payee_name:
        selectedAccount?.business_payee_name || null,
      bank_account_last_four: selectedAccount?.account_last_four || null,
    }));
  };

  const currentBankAccountDisplay = formValues.bank_account_display_name
    ? `${formValues.bank_account_display_name}${
        formValues.bank_account_last_four
          ? ` (****${formValues.bank_account_last_four})`
          : ''
      }`
    : t('dataMappings.bankAccountLinkage.noBankAccountLinked');

  return (
    <Card className='dashboardCard table_card_height'>
      <CardBody
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}>
        <h3 className='subHeading'>
          {t('dataMappings.bankAccountLinkage.title')}
        </h3>

        <Typography variant='body2' style={{ color: '#666', marginBottom: 20 }}>
          {t('dataMappings.bankAccountLinkage.description')}
        </Typography>

        {bankAccountsError && (
          <Alert severity='error' style={{ marginBottom: 20 }}>
            {bankAccountsError}
          </Alert>
        )}

        <GridContainer>
          {!isUpdating('bank-account-linkage') ? (
            <GridItem xs={12} sm={12} md={12}>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  marginBottom: '16px',
                }}>
                <Typography
                  variant='subtitle2'
                  style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  {t('dataMappings.bankAccountLinkage.currentBankAccount')}
                </Typography>
                <Typography variant='body1' style={{ marginBottom: '4px' }}>
                  {currentBankAccountDisplay}
                </Typography>
                {formValues.bank_account_business_payee_name && (
                  <Typography variant='body2' style={{ color: '#666' }}>
                    {t('dataMappings.bankAccountLinkage.businessPayeeName')}:{' '}
                    {formValues.bank_account_business_payee_name}
                  </Typography>
                )}
              </div>
            </GridItem>
          ) : (
            <GridItem xs={12} sm={12} md={12}>
              <div className='custom-select'>
                <FormControl variant='standard' style={{ minWidth: '100%' }}>
                  <InputLabel id='bank-account-select-label'>
                    {t('dataMappings.bankAccountLinkage.selectBankAccount')}
                  </InputLabel>
                  {loadingBankAccounts ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    <Select
                      labelId='bank-account-select-label'
                      value={formValues.client_bank_account_id || ''}
                      onChange={handleBankAccountChange}
                      disabled={!isUpdating('bank-account-linkage')}
                      className='full-width-elem'>
                      <MenuItem value=''>
                        <em>{t('dataMappings.bankAccountLinkage.none')}</em>
                      </MenuItem>
                      {activeBankAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {formatBankAccountDisplay(account)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </div>
            </GridItem>
          )}
        </GridContainer>

        {editId && !updatingBlocks.includes('bank-account-linkage') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={() =>
                setUpdatingBlocks([...updatingBlocks, 'bank-account-linkage'])
              }
              color='primary'>
              Edit
            </Button>
          </div>
        )}
        {updatingBlocks.includes('bank-account-linkage') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={onBankAccountLinkageCancel}
              color='danger'>
              Cancel
            </Button>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={updateBankAccountLinkage}
              color='primary'>
              {t('dataMappings.bankAccountLinkage.save')}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
